import React, { useEffect, useState } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';

// Import all JSON animations
import robotProCon from '../../../../public/media/loaders/robot-pro-con.json';
import robotReadingBook from '../../../../public/media/loaders/robot-reading-book.json';
import robotWaiting from '../../../../public/media/loaders/robot-waiting.json';
import loadingFiles from '../../../../public/media/loaders/loading-files.json';

// Enum for loader types
export enum LoaderType {
  LOADING_FILES = 'loading-files'
}

// Mapping between enum and actual JSON files
const loaderMap: Record<LoaderType, any> = {
  [LoaderType.LOADING_FILES]: loadingFiles
};

// Loader component
interface LayerProps {
  sizePercentage: number;
  loadingText?: string;
  loaderType: LoaderType;
}

const Layer: React.FC<LayerProps> = ({ sizePercentage, loadingText, loaderType }) => {
  const size = (sizePercentage / 100) * 150;
  const animationData = loaderMap[loaderType];

  return (
    <div className="loading-spinner flex flex-col items-center w-full">
      <Player
        autoplay
        loop
        src={animationData}
        style={{ height: `${size}px`, width: `${size}px` }}
      />
      {loadingText && (
        <p className="text-sm text-gray-600 mt-2 text-center w-full animate-glimmer">
          {loadingText}
        </p>
      )}
    </div>
  );
};

const Loading: React.FC<LoadingProps> = ({
  loading,
  sizePercentage = 75,
  loadingText,
  loaderType = LoaderType.LOADING_FILES, // Default animation
  delay = 1000 // Default delay of 1 second, but can be set to 0
}) => {
  const [showLoading, setShowLoading] = useState(false);

  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (loading) {
      if (delay === 0) {
        setShowLoading(true); // Show immediately if delay is 0
      } else {
        timer = setTimeout(() => setShowLoading(true), delay);
      }
    } else {
      setShowLoading(false);
      if (timer) clearTimeout(timer);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [loading, delay]);

  return showLoading ? (
    <Layer sizePercentage={sizePercentage} loadingText={loadingText} loaderType={loaderType} />
  ) : null;
};

// Update interface to allow delay input
interface LoadingProps {
  loading: boolean;
  sizePercentage?: number;
  loadingText?: string;
  loaderType?: LoaderType;
  delay?: number; // Allow custom delay (0 for immediate, default 1000ms)
}

export { Loading };
